import React from "react";
import NotificationDropdown from "./NotificationDropdown";
import UserDropdown from "./UserDropdown";
import { Button } from "@headlessui/react";
import { Link, useLocation } from "react-router-dom";
import { useFirebase } from "../FbContex";
import { activeRooms } from "../lib/selectors";

/** The four nav rows were twelve identical lines each. */
function NavItem({
  to,
  icon,
  label,
  pathname,
  onNavigate,
  sub,
  exact,
}: {
  to: string;
  icon: string;
  label: string;
  pathname: string;
  /** Closes the mobile drawer. A no-op on desktop, where it is never open. */
  onNavigate?: () => void;
  /** Indented, normal-case child row -- the per-room links under Dashboard. */
  sub?: boolean;
  /**
   * Exact match instead of substring. The room rows need it: with slugs
   * `kamar` and `kamar-anak`, a substring match lights up both of them on
   * /room/kamar-anak.
   */
  exact?: boolean;
}) {
  const active =
    exact || to === "/" ? pathname === to : pathname.indexOf(to) !== -1;
  return (
    <li className="items-center">
      <Link
        to={to}
        onClick={onNavigate}
        className={
          (sub
            ? "text-xs normal-case py-2 pl-5 font-medium block "
            : "text-xs uppercase py-3 font-bold block ") +
          (active ? "text-green-700 hover:text-teal-600" : "hover:text-slate-500")
        }
      >
        <i className={`fas fa-${icon} mr-2 text-sm`} /> {label}
      </Link>
    </li>
  );
}

export default function Sidebar() {
  const [open, setOpen] = React.useState(false);
  const { pathname } = useLocation();
  const { signOut, rooms } = useFirebase();
  const drawer = React.useRef<HTMLElement>(null);
  const closeMenu = React.useCallback(() => setOpen(false), []);

  /**
   * Close on a press outside the drawer, or on Escape.
   *
   * The listener is attached from an effect keyed on `open`, so it lands only
   * after the commit that the hamburger's click caused. The pointerdown that
   * opened the drawer has finished dispatching by then and cannot close it
   * again -- which is why the hamburger needs no ref of its own here.
   */
  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!drawer.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function togglrDark() {
    if (document.documentElement.classList.contains("dark"))
      document.documentElement.classList.remove("dark");
    else document.documentElement.classList.add("dark");
  }

  return (
    <>
      <nav className="transition-all duration-1000 md:-top-28 top-0 left-0 w-full fixed h-auto overflow-y-auto flex-row flex-nowrap overflow-hidden shadow-xl items-center justify-between z-30">
        <div className="dark:bg-slate-900 bg-slate-100 px-4 flex flex-wrap items-center justify-between w-full mx-auto">
          <button
            className="cursor-pointer opacity-50 px-3 py-1 text-xl leading-none bg-transparent rounded border border-solid border-transparent"
            type="button"
            onClick={() => setOpen(true)}
          >
            <i className="fas fa-bars"></i>
          </button>
          <Link
            to="/"
            className="md:block inline-block leading-10 whitespace-nowrap text-sm uppercase font-bold p-4 px-0"
          >
            Smart Home
          </Link>

          <ul className="items-center flex flex-wrap list-none">
            <li className="inline-block relative mr-6">
              <NotificationDropdown />
            </li>
            <li className="inline-block relative">
              <UserDropdown />
            </li>
          </ul>
        </div>
      </nav>
      <nav
        ref={drawer}
        className={`transition-transform duration-300 fixed top-0 bottom-0 left-0 p-4 overflow-y-auto overflow-x-hidden w-72 z-40 ${
          open ? "translate-x-0" : "-translate-x-full"
        } md:sticky md:top-0 md:bottom-auto md:left-auto md:h-screen md:shrink-0 md:translate-x-0`}
      >
        <div className="dark:bg-slate-900 bg-slate-100 h-full rounded-lg px-4 md:border-none border outline-offset-1 border-slate-500/50 flex flex-col">
          <div className="mb-4 flex justify-between items-center border-b border-slate-500/50">
            <Link
              to="/"
              className="md:block inline-block leading-10 whitespace-nowrap text-lg uppercase font-bold p-4 px-0"
            >
              Smart Home
            </Link>
            <button
              type="button"
              className="cursor-pointer text-red-600 md:hidden px-1.5 py-1 text-xl leading-none hover:bg-red-900/50 rounded-full"
              onClick={closeMenu}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          {/* Collapse */}
          <div className="flex flex-col items-stretch opacity-100 relative z-40 overflow-y-auto overflow-x-hidden h-auto flex-1 min-h-0">
            {/* Navigation */}

            <ul className="md:flex-col md:min-w-full flex flex-col list-none">
              <NavItem
                to="/"
                icon="tv"
                label="Dashboard"
                pathname={pathname}
                onNavigate={closeMenu}
              />
              {activeRooms(rooms).map((r) => (
                <NavItem
                  key={r.id}
                  to={`/room/${r.slug || r.id}`}
                  icon={r.icon || "door-open"}
                  label={r.name}
                  pathname={pathname}
                  onNavigate={closeMenu}
                  sub
                  exact
                />
              ))}
            </ul>

            {/* Divider */}
            <hr className="my-4 md:min-w-full border-slate-500/50" />
            <ul className="md:flex-col md:min-w-full flex flex-col list-none">
              <NavItem
                to="/charger"
                icon="solar-panel"
                label="charger"
                pathname={pathname}
                onNavigate={closeMenu}
              />
              <NavItem
                to="/temperature"
                icon="temperature-half"
                label="temperature"
                pathname={pathname}
                onNavigate={closeMenu}
              />
              <NavItem
                to="/teras"
                icon="house-chimney-window"
                label="teras"
                pathname={pathname}
                onNavigate={closeMenu}
              />
            </ul>
            <hr className="my-4 md:min-w-full border-slate-500/50" />
            {/* Heading */}
            <h6 className="md:min-w-full text-slate-500 text-xs uppercase font-bold block pt-1 pb-2 no-underline">
              Settings
            </h6>
            {/* Navigation */}
            <ul className="md:flex-col md:min-w-full flex flex-col list-none">
              <NavItem
                to="/rooms"
                icon="person-shelter"
                label="rooms"
                pathname={pathname}
                onNavigate={closeMenu}
              />
              <NavItem
                to="/devices"
                icon="house-laptop"
                label="devices"
                pathname={pathname}
                onNavigate={closeMenu}
              />
              <NavItem
                to="/sources"
                icon="microchip"
                label="sources"
                pathname={pathname}
                onNavigate={closeMenu}
              />
            </ul>

            {/* Divider */}
            <hr className="my-4 md:min-w-full border-slate-500/50" />

            <Button
              onClick={signOut}
              className="text-center gap-2 rounded-md bg-orange-700 py-1.5 px-3 text-sm/6 font-semibold text-white shadow-inner shadow-white/10 focus:outline-none data-[hover]:bg-red-600 data-[open]:bg-red-700 data-[focus]:outline-1 data-[focus]:outline-white"
            >
              Logout
            </Button>
          </div>
          <div className="justify-self-end text-right pb-3">
            <i
              className="fas fa-circle-half-stroke opacity-70 hover:opacity-50 cursor-pointer"
              onClick={togglrDark}
            ></i>
          </div>
        </div>
      </nav>
    </>
  );
}
